import PropTypes from 'prop-types'
import React, { useState, useEffect, useRef } from 'react'
import { withNamespaces } from 'react-i18next'
import TitleBar from '../TitleBar/TitleBar'
import { loadFromLocalStorage } from '../../../helpers/localStorage'

import Step from '../Step/Step'
import Final from '../Final/Final'
import './Exercise.scss'

export const Exercise = ({
  slug,
  codeTemplate,
  name,
  steps,
  completePage = false,
}) => {
  const pageRef = useRef()

  // If local storage is empty, don't mark step index as complete (index -1)
  let storedCompleteStepIndex = loadFromLocalStorage('completedStepIndex')
  if (isNaN(storedCompleteStepIndex) || storedCompleteStepIndex === null) {
    storedCompleteStepIndex = -1
  }

  // If the user has completed the activity, limit it to the last step
  const lastCompletedStep = Math.min(
    storedCompleteStepIndex + 1,
    steps.length - 1
  )

  const [stepIndex, setStepIndex] = useState(
    storedCompleteStepIndex !== -1 ? lastCompletedStep : 0
  )
  // TODO, once step completion is achieved, set this to init to 0 and increment on complete
  const [completedStepIndex, setCompletedStepIndex] = useState(0)

  const getStep = (index) => steps[index || stepIndex]

  useEffect(() => {
    if (completedStepIndex <= 0) return

    if (window.plausible) {
      window.plausible(`B2T Step Complete - Step ${completedStepIndex}`)
    }
  }, [completedStepIndex])

  const previousStep = () => {
    if (stepIndex === 0) return
    if (completePage) {
      window.location = `../${slug}`
    } else {
      setStepIndex(stepIndex - 1)
    }
  }

  const nextStep = () => {
    if (stepIndex === steps.length - 1) {
      const separator = slug.endsWith('/') ? '' : '/'
      window.location = `${slug}${separator}complete`
    } else {
      setStepIndex(stepIndex + 1)
      pageRef.current?.scrollIntoView({ block: 'start' })
    }
  }

  const onStepRun = (stepComplete) => {
    if (stepComplete) {
      setCompletedStepIndex(stepIndex + 1)
    } else {
      setCompletedStepIndex(stepIndex - 1) // e.g. when user changes input after a successful run
    }
  }

  return (
    <div className="c-exercise" ref={pageRef}>
      <TitleBar
        exerciseName={name}
        currentStep={stepIndex + 1}
        complete={completePage}
        totalSteps={steps.length}
        onPreviousStep={previousStep}
        onNextStep={nextStep}
        completedSteps={completedStepIndex + 1}
      />
      {!completePage ? (
        <Step
          exerciseId={slug}
          instructions={getStep().instructions}
          codeTemplate={codeTemplate}
          options={getStep().options}
          stepIndex={stepIndex}
          onNextStep={nextStep}
          onStepRun={onStepRun}
          completed={completedStepIndex > stepIndex}
          isLastStep={stepIndex === steps.length - 1}
        />
      ) : (
        <Final
          exerciseId={slug}
          codeTemplate={codeTemplate}
          stepIndex={steps.length}
          options={{}}
        />
      )}
    </div>
  )
}

Exercise.propTypes = {
  slug: PropTypes.string.isRequired,
  codeTemplate: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  steps: PropTypes.array.isRequired,
  completePage: PropTypes.bool,
}

export default withNamespaces('translations')(Exercise)
